import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Input, InputNumber, Modal } from 'antd';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    PageCard,
    PageShell,
    PageToolbar,
    TechRequestStatusBadge,
} from '../../components/ui';
import {
    completeTechnicianRequest,
    fetchTechnicianRequest,
    fetchTeethes,
    firstApiError,
    formatTechPrice,
    lastReturnReason,
    returnTechnicianRequest,
    startTechnicianRequest,
    toothNumberById,
    updateTechnicianRequestPrice,
} from '../../services/technicianRequestService';

function toastActionError(error, t) {
    const code = error?.response?.status;
    if (code === 401) return;
    if (code === 403) toast.error(t('techRequest.forbidden'));
    else if (code === 404) toast.error(t('techRequest.notFound'));
    else if (code === 409) toast.error(t('techRequest.conflict'));
    else toast.error(firstApiError(error) || t('techRequest.saveError'));
}

export default function TechnicianRequestDetail({ scope }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState(null);
    const [note, setNote] = useState('');
    const [price, setPrice] = useState(0);
    const [saving, setSaving] = useState(false);
    const [teethList, setTeethList] = useState([]);

    const listPath =
        scope === 'doctor'
            ? '/doctor/technician-requests'
            : scope === 'technician'
              ? '/technician/requests'
              : '/admin/technician-requests';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchTechnicianRequest(scope, id);
            setItem(data);
            setPrice(Number(data?.price || 0));
        } catch (error) {
            setItem(null);
            toastActionError(error, t);
        } finally {
            setLoading(false);
        }
    }, [scope, id, t]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        fetchTeethes()
            .then(setTeethList)
            .catch(() => setTeethList([]));
    }, []);

    const openAction = (type) => {
        setAction(type);
        setNote('');
        setPrice(Number(item?.price || 0));
    };

    const handleAction = async () => {
        if (action === 'return' && !note.trim()) {
            toast.error(t('techRequest.returnRequired'));
            return;
        }
        setSaving(true);
        try {
            if (action === 'start') await startTechnicianRequest(id, note.trim());
            else if (action === 'complete') await completeTechnicianRequest(id, note.trim());
            else if (action === 'return') await returnTechnicianRequest(id, note.trim());
            else if (action === 'price') {
                await updateTechnicianRequestPrice(id, {
                    price: Number(price).toFixed(2),
                    description: note.trim(),
                });
            }
            const messages = {
                start: 'techRequest.startedOk',
                complete: 'techRequest.completedOk',
                return: 'techRequest.returnedOk',
                price: 'techRequest.priceUpdated',
            };
            toast.success(t(messages[action]));
            setAction(null);
            load();
        } catch (error) {
            toastActionError(error, t);
        } finally {
            setSaving(false);
        }
    };

    const status = item?.status;
    const returnReason = lastReturnReason(item?.history || []);
    const canStart = scope === 'technician' && (status === 'pending' || status === 'returned');
    const canComplete = scope === 'technician' && status === 'in_progress';
    const canReturn = scope === 'doctor' && status === 'completed';
    const canPrice = scope === 'admin' && item;

    const actionTitle =
        action === 'start'
            ? t('techRequest.start')
            : action === 'complete'
              ? t('techRequest.complete')
              : action === 'return'
                ? t('techRequest.returnWork')
                : t('techRequest.changePrice');

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={`${t('techRequest.requestsTitle')} #${id}`}
                    actions={
                        <button
                            type="button"
                            className="text-sm font-semibold text-slate-500 hover:text-[#1D2C7B]"
                            onClick={() => navigate(listPath)}
                        >
                            {t('common.back')}
                        </button>
                    }
                />

                {loading || !item ? (
                    <p className="px-4 py-8 text-sm text-slate-500">{loading ? t('common.loading') : t('techRequest.notFound')}</p>
                ) : (
                    <div className="space-y-4 px-3 py-4 sm:px-5">
                        <div className="flex flex-wrap items-center gap-2">
                            <TechRequestStatusBadge status={item.status} />
                            <span className="text-xs tabular-nums text-slate-400">
                                {item.created_at ? dayjs(item.created_at).format('DD.MM.YYYY HH:mm') : ''}
                            </span>
                        </div>

                        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                            <div>
                                <dt className="text-xs text-slate-400">{t('common.patient')}</dt>
                                <dd className="m-0 font-medium text-slate-800">
                                    {item.patient?.id && scope !== 'technician' ? (
                                        <Link to={`/patient/${item.patient.id}`} className="text-[#1D2C7B]">
                                            {item.patient.name}
                                        </Link>
                                    ) : (
                                        item.patient?.name ?? t('common.dash')
                                    )}
                                </dd>
                                <dd className="m-0 text-xs text-slate-500">
                                    {[item.patient?.code, item.patient?.phone].filter(Boolean).join(' · ') || t('common.dash')}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs text-slate-400">{t('techRequest.orderedBy')}</dt>
                                <dd className="m-0 font-medium text-slate-800">
                                    {item.doctor?.name ?? item.created_by?.name ?? t('common.dash')}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-xs text-slate-400">{t('techRequest.technician')}</dt>
                                <dd className="m-0 font-medium text-slate-800">{item.technician?.name ?? t('common.dash')}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-slate-400">{t('techRequest.object')}</dt>
                                <dd className="m-0 font-medium text-slate-800">{item.object?.name ?? t('common.dash')}</dd>
                            </div>
                            <div>
                                <dt className="text-xs text-slate-400">{t('techRequest.tooth')}</dt>
                                <dd className="m-0 font-medium tabular-nums text-slate-800">
                                    {toothNumberById(teethList, item.teeth_id) ?? t('common.dash')}
                                </dd>
                            </div>
                        </dl>

                        <div className="rounded-xl border border-slate-200 p-3">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                {t('techRequest.parameters')}
                            </p>
                            {(item.parameters || []).length === 0 ? (
                                <p className="m-0 text-sm text-slate-400">{t('common.dash')}</p>
                            ) : (
                                <ul className="m-0 space-y-1.5 p-0">
                                    {(item.parameters || []).map((param) => (
                                        <li key={`${param.parameter_id}-${param.value}`} className="flex justify-between gap-3 text-sm">
                                            <span className="text-slate-700">
                                                {param.name}: <strong>{param.value}</strong>
                                            </span>
                                            <span className="tabular-nums text-slate-500">
                                                {formatTechPrice(param.price)} {t('common.uzs')}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
                                <p className="m-0 flex justify-between text-slate-500">
                                    <span>{t('techRequest.objectPrice')}</span>
                                    <span className="tabular-nums">
                                        {formatTechPrice(item.object?.price)} {t('common.uzs')}
                                    </span>
                                </p>
                                <p className="m-0 flex justify-between text-slate-500">
                                    <span>{t('techRequest.parametersTotal')}</span>
                                    <span className="tabular-nums">
                                        {formatTechPrice(item.parameters_price)} {t('common.uzs')}
                                    </span>
                                </p>
                                <p className="m-0 flex justify-between font-semibold text-slate-800">
                                    <span>{t('techRequest.finalPrice')}</span>
                                    <span className="tabular-nums text-[#1D2C7B]">
                                        {formatTechPrice(item.price)} {t('common.uzs')}
                                    </span>
                                </p>
                            </div>
                            <p className="mb-0 mt-2 text-xs text-slate-400">{t('techRequest.snapshotNote')}</p>
                        </div>

                        <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                {t('techRequest.description')}
                            </p>
                            <p className="m-0 whitespace-pre-wrap text-sm text-slate-700">{item.description || t('common.dash')}</p>
                        </div>

                        {item.status === 'returned' && returnReason && (
                            <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
                                <p className="mb-0.5 text-xs font-semibold text-red-600">{t('techRequest.lastReturn')}</p>
                                <p className="m-0 text-sm text-red-800">{returnReason}</p>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2">
                            {canStart && (
                                <button
                                    type="button"
                                    className="rounded-lg bg-[#1D2C7B] px-4 py-2 text-sm font-semibold text-white"
                                    onClick={() => openAction('start')}
                                >
                                    {t('techRequest.start')}
                                </button>
                            )}
                            {canComplete && (
                                <button
                                    type="button"
                                    className="rounded-lg bg-[#1D2C7B] px-4 py-2 text-sm font-semibold text-white"
                                    onClick={() => openAction('complete')}
                                >
                                    {t('techRequest.complete')}
                                </button>
                            )}
                            {canReturn && (
                                <button
                                    type="button"
                                    className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600"
                                    onClick={() => openAction('return')}
                                >
                                    {t('techRequest.returnWork')}
                                </button>
                            )}
                            {canPrice && (
                                <button
                                    type="button"
                                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-[#1D2C7B]"
                                    onClick={() => openAction('price')}
                                >
                                    {t('techRequest.changePrice')}
                                </button>
                            )}
                        </div>

                        <div>
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                {t('techRequest.history')}
                            </p>
                            {(item.history || []).length === 0 ? (
                                <p className="m-0 text-sm text-slate-400">{t('techRequest.historyEmpty')}</p>
                            ) : (
                                <ol className="m-0 space-y-2 p-0">
                                    {(item.history || []).map((entry) => (
                                        <li key={entry.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className="text-xs font-semibold text-slate-600">
                                                    {entry.type === 'price_changed'
                                                        ? t('techRequest.priceChanged')
                                                        : t('techRequest.statusChanged')}
                                                </span>
                                                <span className="text-xs tabular-nums text-slate-400">
                                                    {entry.created_at ? dayjs(entry.created_at).format('DD.MM.YYYY HH:mm') : ''}
                                                </span>
                                            </div>
                                            <p className="mb-0 mt-1 text-sm text-slate-700">{entry.user?.name}</p>
                                            {(entry.from_status || entry.to_status) && (
                                                <p className="mb-0 mt-0.5 text-xs text-slate-500">
                                                    {entry.from_status || t('common.dash')} → {entry.to_status || t('common.dash')}
                                                </p>
                                            )}
                                            {(entry.from_price != null || entry.to_price != null) && (
                                                <p className="mb-0 mt-0.5 text-xs tabular-nums text-slate-500">
                                                    {formatTechPrice(entry.from_price)} → {formatTechPrice(entry.to_price)} {t('common.uzs')}
                                                </p>
                                            )}
                                            {entry.description && (
                                                <p className="mb-0 mt-1 whitespace-pre-wrap text-sm text-slate-600">{entry.description}</p>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    </div>
                )}
            </PageCard>

            <Modal
                title={actionTitle}
                open={Boolean(action)}
                onCancel={() => setAction(null)}
                onOk={handleAction}
                confirmLoading={saving}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
            >
                {action === 'price' && (
                    <div className="mb-3">
                        <label className="mb-1 block text-sm font-medium text-slate-700">{t('techRequest.newPrice')}</label>
                        <InputNumber
                            className="!w-full"
                            min={0}
                            max={9999999999.99}
                            precision={2}
                            value={price}
                            onChange={(value) => setPrice(value ?? 0)}
                        />
                    </div>
                )}
                <label className="mb-1 block text-sm font-medium text-slate-700">
                    {action === 'return'
                        ? t('techRequest.returnReason')
                        : action === 'price'
                          ? t('techRequest.priceNote')
                          : t('techRequest.actionNote')}
                </label>
                <Input.TextArea
                    rows={4}
                    maxLength={5000}
                    value={note}
                    placeholder={action === 'return' ? t('techRequest.returnReason') : t('techRequest.actionNotePh')}
                    onChange={(e) => setNote(e.target.value)}
                />
            </Modal>
        </PageShell>
    );
}
